import React, { useEffect, useRef, useState } from "react";
import secureLocalStorage from "react-secure-storage";
import { Button } from "../../components/Button";
import { IProjectGridItem } from "../../@types/project/project-grid-item.type";
import { getAllCostCentersIdsAndNames } from "../../services/api/costCenter/costCenterService";
import {
  DownloadProjectsToExcel,
  getProjectManagersIdsAndNames,
  searchProjectsForGrid,
} from "../../services/api/projects/projectService";
import { getTotalCustomers } from "../../services/api/customers/CustomersService";
import addCircleImage from "./../../assets/_AddCircleIcon_.svg";
import iconClose from "./../../assets/icon-closeModal.svg";
import { IKeyValue } from "../../@types/key-value.type";
import { useNavigate } from "react-router-dom";
import Icon from "../../externalComponent/components/Icon/Icon";
import usePermission from "../../hooks/usePermission";
import { FeatureStatic } from "../../@types/statics/feature.static";
import { getAllProjectsCustomField } from "../../services/api/projectCustomField/projectCustomFieldService";
import { ICustomField } from "../../@types/customField.type";
import styles from "./styles.module.scss";
import { BoardSectionList } from "../../components/DndKit/components/BoardSectionList";
import { IProjectPortfolioFilter } from "../../@types/project/project-portfolio-filter.type";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import { AgGridReact } from "ag-grid-react";
import { themeGrid } from "../../themeGrid";
import { localeText } from "../../localeTextFromGrid";
import { ColDef, FilterChangedEvent, IGetRowsParams } from "ag-grid-community";
import { IProjectDownload, IProjectFilterForGrid } from "../../@types/project/project-filter.type";
import { IGrid } from "../../@types/grid.type";
import iconExcel from "./../../assets/icon-excel.svg";


const columnsDefault: ColDef[] = [
  {
    field: "id",
    headerName: "ID",
    hide: true,
  },
  { field: "projectName", headerName: "Projeto", flex: 1, minWidth: 200 },
  { field: "projectKey", headerName: "Código", flex: 1, minWidth: 200 },
  { field: "customerName", headerName: "Cliente", flex: 1, minWidth: 200 },
  { field: "managerName", headerName: "Responsável", flex: 1, minWidth: 200 },
  {
    field: "costCenterName",
    headerName: "Centro de Custo",
    flex: 1,
    minWidth: 200,
  },
  { field: "status", headerName: "Status", flex: 1, minWidth: 200 },
];

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 0, mt: 2 }}>{children}</Box>}
    </div>
  );
}

export const Projects: React.FC = () => {
  const [searchParamsPortfolio, setSearchParamsPortfolio] =
    useState<IProjectPortfolioFilter>();
  const [columns, setColumns] = useState(columnsDefault);
  const [customers, setCustomers] = useState<IKeyValue[]>([]);
  const [costCenters, setCostCenters] = useState<IKeyValue[]>([]);
  const [managers, setManagers] = useState<IKeyValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [isOpenFilter, setIsOpenFilter] = useState(false);
  const [tabValue, setTabValue] = React.useState(0);
  const gridRef = useRef<AgGridReact<any>>(null);
  let filters = {} as IProjectDownload;

  const permissions = usePermission(FeatureStatic.projects.id);

  const [filteredName, setFilteredName] = useState<string>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsProject")?.toString() || "{}"
      ).projectName || ""
  );
  const [filteredCustomer, setFilteredCustomer] = useState<string | null>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsProject")?.toString() || "{}"
      ).customerIds?.[0] || null
  );
  const [filteredCostCenter, setFilteredCostCenter] = useState<string | null>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsProject")?.toString() || "{}"
      ).costCenterIds?.[0] || null
  );
  const [filteredManager, setFilteredManager] = useState<string | null>(
    () =>
      JSON.parse(
        secureLocalStorage.getItem("searchParamsProject")?.toString() || "{}"
      ).managerIds?.[0] || null
  );

  const navigate = useNavigate();

  const hasFilters = filteredName || filteredCustomer || filteredCostCenter;

  function updatePage(id: string) {
    navigate(`/projects/update/${id}`);
  }

  const getFromCacheGrid = (key: string) => {
    const data = secureLocalStorage.getItem(key);
    return data ? JSON.parse(data as string) : null;
  };

  const [cachedFiltersGrid, setCachedFiltersGrid] = useState(
    getFromCacheGrid("agGridFiltersProjects")
  );
  const [cachedSortGrid, setCachedSortGrid] = useState(
    getFromCacheGrid("agGridSortProjects")
  );

  const saveToCacheGrid = (key: string, data: any) => {
    secureLocalStorage.setItem(key, JSON.stringify(data));
  };

  const onFilterChanged = (params: FilterChangedEvent) => {
    const filters = params.api.getFilterModel(); // Obtém o modelo de filtro
    saveToCacheGrid("agGridFiltersProjects", filters); // Salva no cache
  };

  const onSortChanged = () => {
    const colState = gridRef.current!.api.getColumnState();
    const sortState = colState
      .filter(function (s) {
        return s.sort != null;
      })
      .map(function (s) {
        return { colId: s.colId, sort: s.sort, sortIndex: s.sortIndex };
      });

    saveToCacheGrid("agGridSortProjects", sortState);
  };

  useEffect(() => {
    if (!gridRef.current) return;

    const gridApi = gridRef.current.api;

    if (gridApi) {
      // Restaurar filtros
      if (cachedFiltersGrid) {
        gridApi.setFilterModel(cachedFiltersGrid);
      }

      // Restaurar ordenação
      if (cachedSortGrid) {
        gridApi.applyColumnState({
          state: cachedSortGrid,
          defaultState: { sort: null },
        });
      }
    }
  }, [cachedFiltersGrid, cachedSortGrid, columns]);

  function mapCustomFieldsToTheCollumnGrid(
    customFields: ICustomField[]
  ): ColDef[] {
    return customFields
      ?.filter((item) => item.showInList)
      .map((item) => ({
        id: item.id,
        field: item.name,
        headerName: item.name,
        flex: 1,
        minWidth: 200,
        filter: false,
        sortable: false,
      }));
  }

  function mapCustomFieldsToTheRowGrid(
    projects: IProjectGridItem[]
  ): IProjectGridItem[] {
    return projects.map((project) => {
      const customFieldsObject = Array.isArray(project.customFields)
        ? Object.fromEntries(
            project.customFields.map(({ key, value }) => [key, value])
          )
        : {};

      return {
        ...project,
        ...customFieldsObject,
        customFields: undefined,
      };
    });
  }

  const fetchDataFromFilterFields = async () => {
    try {
      setLoading(true);
      const responseCollumms = await getAllProjectsCustomField();

      const mappedCustomColumns = mapCustomFieldsToTheCollumnGrid(
        responseCollumms.result.items
      );

      setColumns([...columns, ...mappedCustomColumns]);

      const [customersRes, costCentersRes, managerRes] =
        await Promise.allSettled([
          getTotalCustomers(),
          getAllCostCentersIdsAndNames(),
          getProjectManagersIdsAndNames(),
        ]);

      if (customersRes.status === "fulfilled")
        setCustomers(customersRes.value.result);
      if (costCentersRes.status === "fulfilled")
        setCostCenters(costCentersRes.value as IKeyValue[]);
      if (managerRes.status === "fulfilled")
        setManagers(managerRes.value as IKeyValue[]);
    } catch (error) {
      console.error("Erro ao buscar filtros:", error);
    } finally {
      setLoading(false);
    }
  };

  async function searchProjectsPortfolio() {
    const searchParamsProject = {
      name: filteredName?.trim(),
      customerIds: filteredCustomer ? [filteredCustomer] : [],
      costCenterIds: filteredCostCenter ? [filteredCostCenter] : [],
      managersIds: filteredManager ? [filteredManager] : [],
    } as IProjectPortfolioFilter;

    setSearchParamsPortfolio(searchParamsProject);
    secureLocalStorage.setItem(
      "searchParamsProject",
      JSON.stringify(searchParamsProject)
    );
    // const projectsResponse = await searchProjectsToProjectPortfolio(searchParamsProject) as IProjectPortfolioResponse[];
    // setProjectsPortfolio(projectsResponse);
  }

  useEffect(() => {
    setLoading(true);
    const timeoutId = setTimeout(() => {
      if (tabValue === 1) searchProjectsPortfolio();
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [
    filteredName,
    filteredCustomer,
    filteredCostCenter,
    filteredManager,
    tabValue,
  ]);

  useEffect(() => {
    if (!permissions.canView()) {
      navigate("/unauthorized");
      return;
    }
    fetchDataFromFilterFields();
  }, []);

  function clearAndCloseFilter() {
    setIsOpenFilter(false);
    secureLocalStorage.removeItem("searchParamsProject");
    setFilteredName("");
    setFilteredCustomer(null);
    setFilteredCostCenter(null);
    setFilteredManager(null);
  }

  const handleChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      "aria-controls": `simple-tabpanel-${index}`,
    };
  }

  const getDataSource = () => ({
    getRows: async (params: IGetRowsParams) => {
      console.log(params);

      const sortModel = params.sortModel;
      const filterModel = params.filterModel;

      const filter = {
        skip: params.startRow, // Linha inicial para paginação
        take: params.endRow - params.startRow, // Quantidade de linhas
      } as IProjectFilterForGrid;

      if (sortModel.length > 0) {
        filter.orderByField = sortModel[0].colId;
        filter.orderByDirection = sortModel[0].sort == "asc" ? 0 : 1;
      }

      if (filterModel.projectName) {
        filters.name = filterModel.projectName.filter;
        filter.name = filterModel.projectName.filter;
      }else{
        filters.name = undefined;
      }


      if (filterModel.projectKey) {
        filters.code = filterModel.projectKey.filter;
        filter.code = filterModel.projectKey.filter;
      }else{
        filters.code = undefined;
      }


      if (filterModel.customerName) {
        filters.customerName = filterModel.customerName.filter;
        filter.customerName = filterModel.customerName.filter;
      } else{
        filters.customerName = undefined;
      }

      if (filterModel.costCenterName) {
        filters.costCenterName = filterModel.costCenterName.filter;
        filter.costCenterName = filterModel.costCenterName.filter;
      }else{
        filters.costCenterName = undefined;
      }


      if (filterModel.status) {
        filters.statusName = filterModel.status.filter;
        filter.statusName = filterModel.status.filter;
      }else{
        filters.statusName = undefined;
      }


      if (filterModel.managerName) {
        filters.managerName = filterModel.managerName.filter;
        filter.managerName = filterModel.managerName.filter;
      }else{
        filters.managerName = undefined;
      }


      try {
        const response = (await searchProjectsForGrid(
          filter
        )) as IGrid<IProjectGridItem>;

        const customerWithCustomizedFields = mapCustomFieldsToTheRowGrid(
          response.items
        );
        
        params.successCallback(
          customerWithCustomizedFields,
          response.totalRows
        );
      } catch (error) {
        console.error("Erro ao buscar dados:", error);
        params.failCallback(); // Notifica falha no carregamento
      } finally {
        setLoading(false);
      }
    },
  });

  async function ExportToExcel(filters: IProjectDownload) {
    await DownloadProjectsToExcel(filters);
  }

  return (
    <div className={styles.projectsContainer}>
      <div className={styles.header}>
        <h1>Projetos</h1>
        <div className={styles.btnsFilterAndCreate}>
          {tabValue === 1 && (
            <button
              onClick={() => setIsOpenFilter(!isOpenFilter)}
              className={`${styles.btnFilter} ${
                isOpenFilter && styles.isOpenFilter
              }`}
              title="Filtro"
            >
              <Icon
                iconName={hasFilters ? "iconFiltered" : "iconFilter"}
                height="35"
                width="35"
              />
            </button>
          )}
          <Button
            iconUrl={addCircleImage}
            onClick={() => navigate("/projects/create")}
            content="Novo Projeto"
            className={styles.newProjectButton}
          />
        </div>
      </div>

      {isOpenFilter && tabValue === 1 && (
        <div className={styles.filters}>
          <Box
            component="form"
            noValidate
            autoComplete="off"
            className={styles.filterInputs}
          >
            <TextField
              label="Nome / Código do Projeto"
              InputLabelProps={{
                shrink: true,
              }}
              value={filteredName}
              onChange={(e) => {
                const value = e.target.value;
                setFilteredName(value.trim() === "" ? "" : value);
              }}
              fullWidth
              size="small"
              margin="dense"
              placeholder={"Buscar pelo nome ou código do projeto..."}
              sx={{
                fontSize: 14,
                lineHeight: 1.2,
                "& .MuiInputBase-input::placeholder": {
                  fontSize: 12,
                },
              }}
            />

            <Autocomplete
              fullWidth
              options={customers}
              getOptionLabel={(option) => option.value as string}
              value={
                customers.find(
                  (customer) => customer.key === filteredCustomer
                ) || null
              }
              onChange={(event, newValue) =>
                setFilteredCustomer(newValue?.key || "")
              }
              noOptionsText="Sem opções"
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.key}>
                    {option.value}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Cliente"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  margin="dense"
                  placeholder={"Selecione..."}
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.2,
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: 12,
                    },
                  }}
                />
              )}
            />

            <Autocomplete
              fullWidth
              options={costCenters}
              getOptionLabel={(option) => option.value as string}
              value={
                costCenters.find(
                  (costCenter) => costCenter.key === filteredCostCenter
                ) || null
              }
              onChange={(event, newValue) =>
                setFilteredCostCenter(newValue?.key || "")
              }
              noOptionsText="Sem opções"
              renderOption={(props, option) => {
                return (
                  <li {...props} key={option.key}>
                    {option.value}
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Centro de Custo"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  size="small"
                  margin="dense"
                  placeholder={"Selecione..."}
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.2,
                    "& .MuiInputBase-input::placeholder": {
                      fontSize: 12,
                    },
                  }}
                />
              )}
            />

            {tabValue == 1 && !permissions.canEditMine() && (
              <Autocomplete
                fullWidth
                options={managers}
                getOptionLabel={(option) => option.value as string}
                value={
                  managers.find((manager) => manager.key === filteredManager) ||
                  null
                }
                onChange={(event, newValue) =>
                  setFilteredManager(newValue?.key || "")
                }
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.value}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável"
                    InputLabelProps={{
                      shrink: true,
                    }}
                    size="small"
                    margin="dense"
                    placeholder={"Selecione..."}
                    sx={{
                      fontSize: 14,
                      lineHeight: 1.2,
                      "& .MuiInputBase-input::placeholder": {
                        fontSize: 12,
                      },
                    }}
                  />
                )}
              />
            )}

            <button
              className={styles.btnClearFilter}
              onClick={() => clearAndCloseFilter()}
              title="Fechar e limpar filtro"
              type="button"
            >
              <img src={iconClose} alt="" />
            </button>
          </Box>
        </div>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={tabValue}
          onChange={handleChange}
          aria-label="basic tabs example"
          classes={{
            indicator: styles["MuiTabs-indicator"], // Usando colchetes para o nome da classe
          }}
        >
          <Tab label="Lista" {...a11yProps(0)} className={styles.tab} />
          <Tab label="Portfólio" {...a11yProps(1)} className={styles.tab} />
          <Box sx={{marginLeft: "auto", display: "flex"}}>
            <Button
              onClick={() => {
                ExportToExcel(filters);
              }}
              iconUrl={iconExcel}
              className={styles.btnExcel}
            />
          </Box>
        </Tabs>
      </Box>
      <CustomTabPanel value={tabValue} index={0}>
        <div className={`${styles.dataGridContainer}`}>
          <AgGridReact
            ref={gridRef}
            theme={themeGrid}
            localeText={localeText}
            loading={loading}
            columnDefs={columns}
            overlayNoRowsTemplate="<span style='color: gray;'>Sem registros encontrados</span>"
            defaultColDef={{
              sortable: true,
              editable: false,
              filter: true,
              floatingFilter: true,
            }}
            suppressCellFocus={true}
            rowStyle={{ cursor: "pointer" }}
            onRowClicked={(row) => updatePage(row.data.id)}
            cacheBlockSize={50}
            onFilterChanged={onFilterChanged}
            onSortChanged={onSortChanged}
            rowModelType="infinite"
            datasource={getDataSource()}
            animateRows={true}
          />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={tabValue} index={1}>
        {searchParamsPortfolio && (
          <BoardSectionList searchParamsPortfolio={searchParamsPortfolio} />
        )}
      </CustomTabPanel>
    </div>
  );
};
